import {Injectable} from '@angular/core';
import {AuthService} from '../auth.service';
import {TeacherInvitation, UserModel} from '../../models/authentication/user.model';
import {Observable} from 'rxjs';
import {StringExtensionModel} from '../../models/extensions/string-extension.model';
import {map} from 'rxjs/operators';
import {TeacherInvitationFilter, TeacherInvitationModel} from '../../models/authentication/teacher-invitation';
import {TokenModel} from '../../models/authentication/token.model';
import {SchoolModel} from '../../models/school/school.model';

@Injectable({
  providedIn: 'root'
})
export class SchoolAdminTeacherInvitationService {

  constructor(private authSv: AuthService) {
  }

  /**
   * Invites a teacher by email, the required properties in the user model
   * are email, firstName, lastName and schoolAccessType which are used
   * for the invitation.
   *
   * @param user The information about the user to be added.
   */
  inviteTeacher(user: TeacherInvitationModel): Observable<TeacherInvitationModel> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/inviteTeacherNew';

    return this.authSv.post(url, user).pipe(map((response) => {

      return response.data;

    }));
  }

  /**
   * Invites a teacher by email, the required properties in the user model
   * are email, firstName, lastName and schoolAccessType which are used
   * for the invitation.
   *
   * @param user The information about the user to be added.
   */
  inviteTeacherNew(user: UserModel): Observable<UserModel> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/inviteTeacher';

    return this.authSv.post(url, user).pipe(map((response) => {

      return response.data;

    }));
  }

  fetchInvitationTeacher(schoolId: string, token: string): Observable<TeacherInvitationModel> {

    let url = '/schools/' + schoolId;
    url += '/fetchInvitation/' + token;

    return this.authSv.get(url, {}, false).pipe(map((response) => {

      return TeacherInvitationModel.generateModel(response.data);

    }));
  }

  // tslint:disable-next-line:max-line-length
  acceptTeacherInvitation(schoolId: string, token: string, password: string, firstName: string = null, lastName: string = null): Observable<{ user: UserModel, tokens: TokenModel, school: SchoolModel }> {

    const url = `/schools/${schoolId}/acceptInvitation/${token}`;

    const body = {
      password,
      firstName,
      lastName,
      clientInfo: {
        client: 'web',
        clientId: UserModel.getClientId()
      }
    };

    return this.authSv.post(url, body, {}, false).pipe(map((response) => {

      const user = UserModel.generate(response.data.user);
      const tokens = TokenModel.generate(response.data.tokens);
      const school = SchoolModel.generate(response.data.school);

      return {user, tokens, school};

    }));

  }

  fetchTeachersInvitations(filter: TeacherInvitationFilter = null): Observable<TeacherInvitationModel[]> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/teacherInvitations';

    if (filter) {
      url += StringExtensionModel.queryString(filter);
    }

    return this.authSv.get(url).pipe(map((response) => {

      return TeacherInvitationModel.generateModels(response.data);

    }));
  }

  /**
   * You can update the firstName, lastName and schoolAccessType for the teacherInvitation.
   *
   * @param teacherInvitation The teacherInvitation model to update.
   */
  updateTeacherInvitation(teacherInvitation: TeacherInvitationModel): Observable<TeacherInvitationModel> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/teachersInvitations/' + teacherInvitation._id;

    return this.authSv.patch(url, teacherInvitation).pipe(map((response) => {

      return TeacherInvitationModel.generateModel(response.data);

    }));

  }

  resendTeacherInvitation(userId: string): Observable<TeacherInvitationModel> {

    const sId = SchoolModel.getCurrent()._id;
    const url = `/schools/${sId}/teacherInvitations/${userId}/resend`;

    return this.authSv.get(url).pipe(map((response) => {

      const teacherInvitation = TeacherInvitationModel.generateModel(response.data);
      return teacherInvitation;

    }));
  }

  getSetupLink(userId: string): Observable<{ url: string, invitation: TeacherInvitationModel }> {

    const sId = SchoolModel.getCurrent()._id;
    const url = `/schools/${sId}/teacherInvitations/${userId}/setupLink`;

    return this.authSv.get(url).pipe(map((response) => {

      return {
        url: response.data.url,
        invitation: TeacherInvitationModel.generateModel(response.data.invitation)
      };

    }));
  }

  /**
   * Only uses the amil, firstName and lastName fields which can be updated
   * to create teacher invitation from each model.
   *
   * @param teachersInvitations A list of student models to be created.
   */
  importTeachersInvitations(teachersInvitations: TeacherInvitation[]): Observable<TeacherInvitationModel[]> {

    let url = '/schools/' + SchoolModel.getCurrent()._id;
    url += '/importTeachersInvitations';

    return this.authSv.post(url, teachersInvitations).pipe(map((response) => {

      return TeacherInvitationModel.generateModels(response.data);

    }));

  }
}
